import './Tooltip.css'

import {
  arrow,
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useFloating,
} from '@floating-ui/react-dom'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cx } from '../utilities/cx'
import { defaultOverlayRootName, getOverlayRootElement } from './OverlayRoot'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

export type TooltipProps = {
  className?: string
  content: React.ReactNode
  position?: TooltipPosition
  delay?: number
  overlayRootName?: string
  children: React.ReactNode
}

export const Tooltip = (props: TooltipProps) => {
  const {
    className,
    content,
    position = 'top',
    delay = 200,
    overlayRootName = defaultOverlayRootName,
    children,
  } = props

  const [isVisible, setIsVisible] = useState(false)
  const timeoutRef = useRef<number | null>(null)
  const arrowRef = useRef<HTMLDivElement>(null)

  const { refs, floatingStyles, middlewareData, placement } = useFloating({
    placement: position as Placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
  })

  const handleMouseEnter = () => {
    timeoutRef.current = window.setTimeout(() => {
      setIsVisible(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsVisible(false)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const staticSideMap: Record<string, string> = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }
  const staticSide = staticSideMap[placement.split('-')[0]!]!

  const arrowStyle: React.CSSProperties = {
    ...(middlewareData.arrow && {
      left: middlewareData.arrow.x != null ? middlewareData.arrow.x : undefined,
      top: middlewareData.arrow.y != null ? middlewareData.arrow.y : undefined,
    }),
    [staticSide]: -4,
  }

  const tooltipClassNames = cx('tooltip__content', `tooltip__content--${placement.split('-')[0]}`)

  const tooltipElement = isVisible ? (
    <div ref={refs.setFloating} className={tooltipClassNames} role="tooltip" style={floatingStyles}>
      {content}
      <div ref={arrowRef} className="tooltip__arrow" style={arrowStyle} />
    </div>
  ) : null

  return (
    <div
      className={cx('tooltip', className)}
      ref={refs.setReference}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {tooltipElement && createPortal(tooltipElement, getOverlayRootElement(overlayRootName)!)}
    </div>
  )
}
